import { Action, handleActions } from 'redux-actions'
import { takeLatest } from 'redux-saga/effects'
import { oc } from 'ts-optchain'

import { fetchAccountCount, fetchSimpleAccountList } from '@/api/AccountApi'
import {
  EmptyAccountCountResponse,
  IAccountCountResponse, IQuickCreditParams,
  ISimpleAccountList,
  ISimpleAccountResponse, QUICK_CREDIT_STATUS_CODE,
} from '@/domains/AccountDomain'
import { IRootState } from '@/modules'
import { createFetchAction, createSaga } from '@/utils/actionUtils'
import { apiFailureReducer, apiSuccessReducer } from '@/utils/reducerUtils'

export const SIMPLE_ACCOUNT = 'SimpleAccount'
export const SIMPLE_ACCOUNT_UPDATE = 'SimpleAccountUpdate'
export const ACCOUNT_COUNT = 'AccountCount'

type IAccountModuleState = ISimpleAccountList & { accountCount: IAccountCountResponse }

const initialState: IAccountModuleState = {
  list: [],
  accountCount: EmptyAccountCountResponse,
  qcExistYn: true,
  creditJan: '',
  overdueYn: false,
  qcStatusTypeCode: QUICK_CREDIT_STATUS_CODE.NORMAL,
}

export const simpleAccountAsync = createFetchAction(SIMPLE_ACCOUNT)
export const simpleAccountUpdateAsync = createFetchAction(SIMPLE_ACCOUNT_UPDATE)
export const accountCountAsync = createFetchAction(ACCOUNT_COUNT)

export const reducer = {
  [simpleAccountAsync.SUCCESS]: apiSuccessReducer,
  [simpleAccountAsync.FAILURE]: apiFailureReducer,
  [simpleAccountUpdateAsync.SUCCESS]: apiSuccessReducer,
  [simpleAccountUpdateAsync.FAILURE]: apiFailureReducer,
  [accountCountAsync.SUCCESS]: (
    state: IAccountModuleState,
    action: Action<IAccountCountResponse>,
  ) => {
    return {
      ...state,
      accountCount: action.payload,
    }
  },
  [accountCountAsync.FAILURE]: apiFailureReducer,
}

export const simpleAccountSelector = {
  simpleAccounts: (state: IRootState) => oc(state[SIMPLE_ACCOUNT]).list([]),
  majorAccount: (state: IRootState) => {
    const list = oc(state[SIMPLE_ACCOUNT]).list([])
    const majorAccount = list.find((item: ISimpleAccountResponse) => item.dpAcctYn)

    return majorAccount || list[0]
  },
  availableToIssueDebitCard: (state: IRootState): ISimpleAccountResponse | null => {
    const accounts = oc(state[SIMPLE_ACCOUNT]).list([])
    const availableAccount = accounts.find(
      (account: ISimpleAccountResponse) => !account.debitCardYn,
    )

    if (!availableAccount) {
      return null
    }

    return availableAccount
  },
  totSaving: (state: IRootState): number => oc(state[SIMPLE_ACCOUNT]).accountCount.totSaving(0),
  hasOnlyOneAccount: (state: IRootState): boolean => simpleAccountSelector.totSaving(state) === 1,
  getQuickCreditInfo: (state: IRootState): IQuickCreditParams => {
    const simpleAccountState = state[SIMPLE_ACCOUNT]
    return {
      creditJan: simpleAccountState.creditJan,
      overdueYn: simpleAccountState.overdueYn,
      qcExistYn: simpleAccountState.qcExistYn,
      qcStatusTypeCode: simpleAccountState.qcStatusTypeCode
    }
  }
}

export const simpleAccountReducer = handleActions(reducer, initialState)
export const simpleAccountSaga = [
  takeLatest(
    simpleAccountAsync.FETCH,
    createSaga<ISimpleAccountList>(simpleAccountAsync, fetchSimpleAccountList),
  ),
  takeLatest(
    simpleAccountUpdateAsync.FETCH,
    createSaga<ISimpleAccountList>(simpleAccountUpdateAsync, fetchSimpleAccountList),
  ),
  takeLatest(
    accountCountAsync.FETCH,
    createSaga<IAccountCountResponse>(accountCountAsync, fetchAccountCount),
  ),
]
